/**
 * Chat event handlers hook
 * Manages all event handlers with useCallback for stability
 */

import { useCallback, useRef } from 'react';
import { logger } from '../../lib/logger';
import { shouldSubmitOnEnter } from '../utils/composerKeys';

interface UseChatHandlersProps {
  input: string;
  /** True while a request is in flight OR streaming (submitted|streaming). */
  isBusy: boolean;
  pendingToolCallConfirmation: boolean;
  sendMessage: (message: {
    text: string;
    files?: any[];
    activeSkillId?: string;
  }) => Promise<void>;
  setInput: (input: string) => void;
  addToolApprovalResponse: (response: { id: string; approved: boolean; reason?: string }) => void;
  /** Marketplace skill attached via the composer picker (sent as body.activeSkillId). */
  activeSkillId?: string | null;
}

interface UseChatHandlersReturn {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>, files?: any[]) => Promise<void>;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleToolApprovalResponse: (approvalId: string, approved: boolean, reason?: string) => void;
  handleSuggestionClick: (suggestion: string) => void;
}

export function useChatHandlers({
  input,
  isBusy,
  pendingToolCallConfirmation,
  sendMessage,
  setInput,
  addToolApprovalResponse,
  activeSkillId = null,
}: UseChatHandlersProps): UseChatHandlersReturn {
  // Synchronous in-flight latch. `isBusy` (derived from useChat `status`) does
  // not flip until React re-renders, so two Enter keydowns fired in the same
  // frame — or Enter + a Send click — both pass the `!isBusy` gate and double-send.
  // This ref flips immediately and blocks the second entry.
  const submittingRef = useRef(false);

  // Single submission path for BOTH the Send button and Enter (see handleKeyDown,
  // which routes through form.requestSubmit). Keeping one path means attachments,
  // the double-send guard, and input-preservation-on-failure all apply uniformly.
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>, files?: any[]) => {
      e.preventDefault();
      if (submittingRef.current) return;

      const hasText = input.trim().length > 0;
      const hasFiles = !!files && files.length > 0;
      // Allow sending if text is present OR files are attached.
      // A skill badge alone is not enough — the user must provide instruction text
      // (or a file) so Meridian knows what to do with the playbook.
      if (!(hasText || hasFiles) || isBusy || pendingToolCallConfirmation) return;

      submittingRef.current = true;
      try {
        await sendMessage({
          text: input,
          files,
          activeSkillId: activeSkillId ?? undefined,
        });
        // Only clear AFTER a successful send so a failure preserves the user's text.
        setInput('');
      } catch (error) {
        logger.error('error sending message', error);
      } finally {
        submittingRef.current = false;
      }
    },
    [input, sendMessage, setInput, isBusy, pendingToolCallConfirmation, activeSkillId]
  );

  // Enter sends, Shift+Enter inserts a newline, and an IME composition Enter is
  // left for the IME (CJK-safe). We delegate to the form's submit so the Enter
  // path is identical to the button path — same attachments, same guards.
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (shouldSubmitOnEnter(e)) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  }, []);

  // Handle tool approval (SDK pattern: addToolApprovalResponse; sendAutomaticallyWhen triggers send)
  const handleToolApprovalResponse = useCallback(
    (approvalId: string, approved: boolean, reason?: string) => {
      addToolApprovalResponse({ id: approvalId, approved, reason });
    },
    [addToolApprovalResponse]
  );

  // Handle suggested message click
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setInput(suggestion);
    },
    [setInput]
  );

  return {
    handleSubmit,
    handleKeyDown,
    handleToolApprovalResponse,
    handleSuggestionClick,
  };
}
